import type { AccusationPhaseState } from "@cold-case/shared/accusation-phase";
import { TRIO_ACCUSATION_MAJORITY } from "@cold-case/shared/constants";

function escapeHtml(s: string): string {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export function accuseTabIntro(playerCount: number): string {
  const majority =
    playerCount <= 2
      ? "Both players must vote <strong>Yes</strong>."
      : `At least <strong>${TRIO_ACCUSATION_MAJORITY} of 3</strong> players must vote <strong>Yes</strong>.`;
  return `<div class="acc-phase-callout acc-accuse-intro">
<p><strong>Team accusation</strong></p>

<p><strong>1. Start a team vote</strong></p>
<p>When your team is ready, tap <strong>Start an accusation</strong>.</p>
<ul class="acc-accuse-intro-list">
<li>Everyone gets a short vote. ${majority}</li>
<li>If the vote passes, you enter a shared accusation screen with step-by-step instructions.</li>
</ul>

<p><strong>2. Lock in your arrest</strong></p>
<p>On that screen you will:</p>
<ul class="acc-accuse-intro-list">
<li><strong>Review</strong> the case together</li>
<li><strong>Choose</strong> a suspect (each detective picks one)</li>
<li><strong>Align</strong> on the same person</li>
<li>Everyone taps <strong>Agree to Request Warrant</strong> to lock in the arrest</li>
</ul>

<p class="acc-one-shot-reminder"><strong>Remember: You only have one shot at naming the correct suspect!</strong></p>
</div>`;
}

export function voteModalBody(proposerName: string, playerCount: number): string {
  const rule =
    playerCount <= 1
      ? "Confirm to proceed."
      : "Both players must vote Yes to proceed.";
  return `<p class="acc-vote-lead"><strong>${escapeHtml(proposerName)}</strong> wants to start an accusation!</p>
<p>Are you ready?</p>
<p class="acc-vote-sub">${escapeHtml(rule)} All players must vote to proceed.</p>`;
}

export function arrestInstructionsHeader(): string {
  return `<div class="acc-phase-callout acc-arrest-header">
<p><strong>Accusation in progress</strong></p>
<ol class="acc-arrest-steps">
<li><strong>Review</strong> the case briefing, timeline, and clues (<em>Case &amp; Clues</em> tab).</li>
<li><strong>Choose</strong> who you think is responsible (<em>Suspects</em> tab — tap a portrait).</li>
<li><strong>Align</strong> with your partner(s) on <em>Make an Arrest</em> — use chat if you disagree.</li>
<li>When everyone has picked the <strong>same suspect</strong>, each player taps <strong>Agree to Request Warrant</strong> to lock in.</li>
</ol>
<p class="acc-arrest-timeout-note">You have 3 minutes. If you do not all agree in time, the case is lost and your team returns to the lobby.</p>
</div>`;
}

export function arrestTabIntro(tab: "clues" | "suspects" | "arrest"): string {
  switch (tab) {
    case "clues":
      return `<p class="acc-tab-intro">Read-only recap. Everything you have found so far. Use this to compare alibis and evidence before you choose a suspect.</p>`;
    case "suspects":
      return `<p class="acc-tab-intro">Tap one person to mark them as <strong>your</strong> choice. Other detectives see your pick on <em>Make an Arrest</em>. You can change your pick until you lock a warrant.</p>`;
    case "arrest":
      return `<p class="acc-tab-intro">See what each detective chose. To match someone else, tap <strong>Pick …'s Suspect</strong>. When all names show the <strong>same</strong> suspect, everyone taps <strong>Agree to Request Warrant</strong>.</p>`;
  }
}

export type ArrestStatusInput = {
  phase: AccusationPhaseState;
  selfUserId: string;
  players: { userId: string; displayName: string }[];
  suspectName: (instanceId: string | null) => string;
};

export function arrestDynamicStatus(input: ArrestStatusInput): string {
  const { phase, selfUserId, players, suspectName } = input;
  const ids = players.map((p) => p.userId);
  const selfPick = phase.picks[selfUserId] ?? null;
  if (!selfPick) {
    return "Go to <strong>Suspects</strong> and choose who you believe is responsible.";
  }
  const waiting = ids.filter((id) => !phase.picks[id]);
  if (waiting.length > 0) {
    const names = waiting
      .map((id) => players.find((p) => p.userId === id)?.displayName ?? "A detective")
      .join(", ");
    return `Waiting for ${escapeHtml(names)} to choose a suspect…`;
  }
  const picks = ids.map((id) => phase.picks[id]!);
  const allSame = picks.every((p) => p === picks[0]);
  if (!allSame) {
    return "Not everyone agrees yet. Talk it out in chat, or tap <strong>Pick …'s Suspect</strong> to match a partner.";
  }
  const agreedName = suspectName(picks[0]);
  if (phase.warrantLocked[selfUserId]) {
    const others = ids.filter((id) => id !== selfUserId && !phase.warrantLocked[id]);
    if (others.length === 0) {
      return "All detectives locked in. Resolving case…";
    }
    const names = others
      .map((id) => players.find((p) => p.userId === id)?.displayName ?? "A detective")
      .join(", ");
    return `You locked your warrant. Waiting for ${escapeHtml(names)}…`;
  }
  return `Everyone agrees on <strong>${escapeHtml(agreedName)}</strong>. Tap <strong>Agree to Request Warrant</strong> when you are ready to lock in.`;
}

/** CSS class for Make an Arrest status banner. */
export function arrestStatusTone(input: ArrestStatusInput): "warn" | "ready" | "locked" | "neutral" {
  const { phase, selfUserId, players } = input;
  const ids = players.map((p) => p.userId);
  if (phase.warrantLocked[selfUserId]) {
    const others = ids.filter((id) => id !== selfUserId && !phase.warrantLocked[id]);
    return others.length === 0 ? "locked" : "neutral";
  }
  if (!phase.picks[selfUserId]) return "warn";
  const picks = ids.map((id) => phase.picks[id]);
  if (picks.some((p) => !p) || !picks.every((p) => p === picks[0])) return "warn";
  return "ready";
}

export function warrantDisabledReason(input: ArrestStatusInput): string | null {
  const { phase, selfUserId, players } = input;
  const ids = players.map((p) => p.userId);
  if (phase.warrantLocked[selfUserId]) return "You already agreed to the warrant.";
  if (!phase.picks[selfUserId]) return "Choose a suspect on the Suspects tab first.";
  const picks = ids.map((id) => phase.picks[id]);
  if (picks.some((p) => !p)) return "Waiting for all detectives to choose a suspect.";
  if (!picks.every((p) => p === picks[0])) {
    return "All detectives must pick the same person before requesting a warrant.";
  }
  return null;
}
